/**
 * Fill-in answer matching helpers.
 *
 * Goals:
 * - Allow author-defined alternate answers.
 * - Normalize user input and answers to be resilient to punctuation,
 *   whitespace, hyphenation, casing, and apostrophes.
 */

function pushUnique(target, value) {
  const normalized = String(value || '').trim();
  if (!normalized) return;

  const exists = target.some((item) => item.toLowerCase() === normalized.toLowerCase());
  if (!exists) {
    target.push(normalized);
  }
}

function splitDelimitedValues(raw) {
  return String(raw || '')
    .split(/\r?\n|,|;|\|/g)
    .map((part) => String(part || '').trim())
    .filter(Boolean);
}

export function extractFillInAcceptedAnswers(correctAnswer) {
  const accepted = [];

  const addFromValue = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return;

    // Support explicit delimiter-based alternates.
    const delimited = splitDelimitedValues(raw);
    if (delimited.length > 1) {
      delimited.forEach((item) => pushUnique(accepted, item));
      return;
    }

    pushUnique(accepted, raw);
  };

  if (Array.isArray(correctAnswer)) {
    correctAnswer.forEach((value) => addFromValue(value));
  } else {
    addFromValue(correctAnswer);
  }

  return accepted;
}

export function normalizeFillInText(value) {
  if (value == null) return '';

  return String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[\u2019'`]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, '');
}

export function isFillInAnswerCorrect(userAnswer, correctAnswer) {
  const normalizedUser = normalizeFillInText(userAnswer);
  if (!normalizedUser) return false;

  const normalizedAccepted = extractFillInAcceptedAnswers(correctAnswer)
    .map((answer) => normalizeFillInText(answer))
    .filter(Boolean);

  if (normalizedAccepted.length === 0) return false;

  return normalizedAccepted.includes(normalizedUser);
}

export function parseFillInAuthoringValue(rawValue) {
  const accepted = [];

  const addFromValue = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return;

    const delimited = splitDelimitedValues(raw);
    if (delimited.length > 1) {
      delimited.forEach((item) => pushUnique(accepted, item));
      return;
    }

    pushUnique(accepted, raw);
  };

  if (Array.isArray(rawValue)) {
    rawValue.forEach((value) => addFromValue(value));
  } else {
    addFromValue(rawValue);
  }

  if (accepted.length <= 1) {
    return accepted[0] || '';
  }

  return accepted;
}
